import { adminAPI } from '../services/api';

export interface AdminDrugOption {
  value: string;
  label: string;
  active: boolean;
  aliases: string[];
  raw: Record<string, unknown>;
}

const pickFirstString = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value !== 'string') {
      continue;
    }
    const normalized = value.trim();
    if (normalized) {
      return normalized;
    }
  }
  return '';
};

const normalizeDrugOption = (row: unknown): AdminDrugOption | null => {
  if (!row || typeof row !== 'object') {
    return null;
  }

  const source = row as Record<string, unknown>;
  const value = pickFirstString(
    source.drug_id,
    source.value,
    source.id,
    source.name,
    source.drug_name,
  );

  if (!value) {
    return null;
  }

  const label = pickFirstString(
    source.display_name,
    source.drug_name_cn,
    source.drug_name,
    source.name,
    source.label,
    source.drug_id,
    source.value,
    source.id,
  ) || value;

  const status = pickFirstString(source.status);
  const active = status
    ? status !== 'inactive'
    : (typeof source.is_active === 'boolean' ? source.is_active : true);

  const aliases = Array.from(new Set([
    value,
    label,
    pickFirstString(source.drug_id),
    pickFirstString(source.drug_name),
    pickFirstString(source.drug_name_cn),
    pickFirstString(source.drug_name_en),
    pickFirstString(source.name),
  ].filter(Boolean)));

  return {
    value,
    label,
    active,
    aliases,
    raw: source,
  };
};

const mergeDrugOptions = (rows: unknown[], sink: Map<string, AdminDrugOption>) => {
  for (const row of rows) {
    const normalized = normalizeDrugOption(row);
    if (!normalized) {
      continue;
    }

    const key = normalized.value.toLowerCase();
    const existing = sink.get(key);
    if (!existing) {
      sink.set(key, normalized);
      continue;
    }

    sink.set(key, {
      ...existing,
      label: existing.label === existing.value && normalized.label !== normalized.value
        ? normalized.label
        : existing.label,
      active: existing.active || normalized.active,
      aliases: Array.from(new Set([...existing.aliases, ...normalized.aliases])),
      raw: { ...existing.raw, ...normalized.raw },
    });
  }
};

export async function loadAdminDrugOptions(options?: { activeOnly?: boolean }): Promise<AdminDrugOption[]> {
  const activeOnly = options?.activeOnly ?? false;
  const results = await Promise.allSettled([
    adminAPI.getDrugOptions(),
    adminAPI.listDrugParams(),
  ]);

  const allRejected = results.every(result => result.status === 'rejected');
  if (allRejected) {
    const firstFailure = results.find(
      (result): result is PromiseRejectedResult => result.status === 'rejected',
    );
    throw firstFailure?.reason ?? new Error('加载药物选项失败');
  }

  const merged = new Map<string, AdminDrugOption>();
  for (const result of results) {
    if (result.status !== 'fulfilled') {
      continue;
    }
    const rows = Array.isArray(result.value?.drugs) ? result.value.drugs : [];
    mergeDrugOptions(rows, merged);
  }

  return Array.from(merged.values())
    .filter(option => !activeOnly || option.active)
    .sort((left, right) => left.label.localeCompare(right.label, 'zh-CN'));
}
